import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { Badge, Icon } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { AD_TYPE_LABEL, int, isVideo, relativeDay } from "./formatters";
import FormatBadge from "./FormatBadge";
import PlayOverlay from "./PlayOverlay";

const useStyles = makeStyles({
  row: {
    display: "flex",
    gap: 12,
    padding: "12px 0",
    borderBottom: `1px solid ${tokens.divider}`,
  },
  rowClickable: {
    cursor: "pointer",
    "&:hover": { background: "#F6F8FA" },
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 8,
    flexShrink: 0,
    objectFit: "cover",
    background: tokens.inputFill,
  },
  thumbFallback: {
    width: 44,
    height: 44,
    borderRadius: 8,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: tokens.primaryLight,
    color: tokens.primary,
  },
  body: { flex: 1, minWidth: 0 },
  topLine: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  name: {
    fontFamily: tokens.fontBody,
    fontWeight: 600,
    fontSize: 13.5,
    color: "#232D2F",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 260,
  },
  headline: {
    fontFamily: tokens.fontBody,
    fontSize: 12,
    color: tokens.textSecondary,
    marginTop: 3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  stats: {
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
    marginTop: 7,
  },
  stat: { minWidth: 58 },
  statValue: {
    fontFamily: tokens.fontBody,
    fontWeight: 700,
    fontSize: 13,
    color: tokens.textPrimary,
    fontVariantNumeric: "tabular-nums",
  },
  statLabel: {
    fontFamily: tokens.fontBody,
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: tokens.textHint,
    marginTop: 1,
  },
  meta: {
    fontFamily: tokens.fontBody,
    fontSize: 11,
    color: tokens.textHint,
    marginTop: 6,
  },
  link: {
    color: tokens.link,
    fontFamily: tokens.fontBody,
    fontSize: 11.5,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
});

const statusTone = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "ACTIVE") return "success";
  if (value === "PAUSED") return "warning";
  if (value.includes("DISAPPROVED") || value.includes("REJECTED")) return "error";
  return "neutral";
};

const Stat = ({ classes, value, label }) => (
  <Box className={classes.stat}>
    <Typography component="div" className={classes.statValue}>{value}</Typography>
    <Typography component="div" className={classes.statLabel}>{label}</Typography>
  </Box>
);

export default function AdDetailList({ ads = [], onSelect }) {
  const classes = useStyles();

  return (
    <Box>
      {ads.map((ad) => {
        const status = ad.adEffectiveStatus || ad.adStatus;
        const variantIndex = ad.adTemplate && ad.adTemplate.variantIndex;
        return (
          <Box
            key={ad._id}
            className={`${classes.row} ${onSelect ? classes.rowClickable : ""}`}
            data-testid={`ad-detail-${ad._id}`}
            onClick={onSelect ? () => onSelect(ad) : undefined}
          >
            {ad.imageUrl ? (
              <PlayOverlay show={isVideo(ad)} size={22}>
                <img src={ad.imageUrl} alt="" className={classes.thumb} />
              </PlayOverlay>
            ) : (
              <Box className={classes.thumbFallback}>
                <Icon name="layout-grid" size={18} />
              </Box>
            )}

            <Box className={classes.body}>
              <Box className={classes.topLine}>
                <Typography component="div" className={classes.name} title={ad.adName || ""}>
                  {ad.adName || "Untitled ad"}
                </Typography>
                <Badge tone={statusTone(status)} subtle>{status || "—"}</Badge>
                <Badge tone="neutral" subtle>
                  {AD_TYPE_LABEL[ad.adType] || ad.adType || "—"}
                </Badge>
                <FormatBadge item={ad} />
                {Number.isInteger(variantIndex) ? (
                  <Badge tone="info" subtle>{`Variant ${variantIndex + 1}`}</Badge>
                ) : null}
              </Box>

              {ad.headline ? (
                <Typography component="div" className={classes.headline} title={ad.headline}>
                  {ad.headline}
                </Typography>
              ) : null}

              <Box className={classes.stats}>
                <Stat classes={classes} value={int(ad.spend)} label="Spend" />
                <Stat classes={classes} value={int(ad.impressions)} label="Impressions" />
                <Stat classes={classes} value={int(ad.reach)} label="Reach" />
                <Stat classes={classes} value={int(ad.clicks)} label="Clicks" />
                <Stat classes={classes} value={int(ad.messages)} label="Messages" />
                <Stat classes={classes} value={int(ad.leads)} label="Leads" />
              </Box>

              <Typography component="div" className={classes.meta}>
                Live {relativeDay(ad.createdAt)}
                {ad.adId ? ` · Ad ${ad.adId}` : ""}
                {ad.dailyBudget ? ` · Daily ${int(ad.dailyBudget)}` : ""}
              </Typography>

              {ad.previewLink ? (
                <a
                  className={classes.link}
                  href={ad.previewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`ad-preview-${ad._id}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Icon name="external-link" size={13} />
                  See on Meta
                </a>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
